import React, { useRef, useState, useEffect } from "react";
import { MdDownload, MdRotateLeft, MdUpload, MdAdd, MdDelete } from "react-icons/md";

import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";

function Testing() {
  const [qrPosition, setQrPosition] = useState({ x: 50, y: 50 });
  const [qrSize, setQrSize] = useState({ width: 35, height: 35 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [qrImage, setQrImage] = useState(null);
  const [bgColor, setBgColor] = useState("#ffffff");
  const [bgImage, setBgImage] = useState(null);
  const [textElements, setTextElements] = useState([]);
  const [images, setImages] = useState([]);
  const [draggingTextId, setDraggingTextId] = useState(null);
  const [draggingImageId, setDraggingImageId] = useState(null);
  const [selectedTextId, setSelectedTextId] = useState(null);
  const badgeRef = useRef(null);
  const imageInputRef = useRef(null);
  const bgImageInputRef = useRef(null);

  const [qrText, setQrText] = useState("https://example.com");

  const BADGE_WIDTH = 384;
  const BADGE_HEIGHT = 576;

  const fontFamilies = [
    "Arial",
    "Times New Roman",
    "Courier New",
    "Georgia",
    "Verdana",
    "Comic Sans MS",
    "Impact",
    "Trebuchet MS",
    "Palatino",
    "Garamond",
  ];

  const fontWeights = [
    { label: "Light", value: "300" },
    { label: "Normal", value: "400" },
    { label: "Medium", value: "500" },
    { label: "Semi Bold", value: "600" },
    { label: "Bold", value: "700" },
    { label: "Extra Bold", value: "800" },
  ];

  useEffect(() => {
    if (qrText) {
      import("qrcode")
        .then((QRCode) => {
          QRCode.default
            .toDataURL(qrText, { width: 300, margin: 1 })
            .then((url) => setQrImage(url))
            .catch((err) => console.error("QR Code generation error:", err));
        });
    } else {
      setQrImage(null);
    }
  }, [qrText]);

  useEffect(() => {
    if (textElements.length > 0 && !selectedTextId) {
      const last = textElements[textElements.length - 1];
      setSelectedTextId(last.id);
    }
  }, [textElements, selectedTextId]);

  const handleImageUpload = (e) => {
    const files = e.target.files;
    if (files?.length) {
      Array.from(files).forEach((file) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const newImg = {
            id: Date.now().toString() + Math.random(),
            src: ev.target?.result,
            x: 100,
            y: 100,
            width: 100,
            height: 100,
          };
          setImages((prev) => [...prev, newImg]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleBgImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setBgImage(ev.target?.result);
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = (id) => setImages((prev) => prev.filter((i) => i.id !== id));

  const handleAddText = () => {
    const newText = {
      id: Date.now().toString() + Math.random(),
      text: "Double click to edit",
      x: 150,
      y: 150,
      fontSize: 28,
      fontFamily: "Arial",
      color: "#000000",
      fontWeight: "700",
      textAlign: "left",
    };
    setTextElements((prev) => [...prev, newText]);
  };

  const handleTextChange = (id, newVal) =>
    setTextElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, text: newVal } : el))
    );

  const handleTextStyleChange = (id, prop, val) =>
    setTextElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, [prop]: val } : el))
    );

  const handleDeleteText = (id) => {
    setTextElements((prev) => prev.filter((el) => el.id !== id));
    if (selectedTextId === id) setSelectedTextId(null);
  };

  const handleMouseDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setIsDragging(true);
  };

  const handleTextMouseDown = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setDraggingTextId(id);
    setSelectedTextId(id);
  };

  const handleImageMouseDown = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setDraggingImageId(id);
  };

  const handleResizeMouseDown = (e) => {
    e.stopPropagation();
    setIsResizing(true);
    setDragOffset({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!badgeRef.current) return;
    const badgeRect = badgeRef.current.getBoundingClientRect();
    const scale = badgeRect.width / BADGE_WIDTH;

    if (isResizing) {
      const deltaX = (e.clientX - dragOffset.x) / scale;
      const deltaY = (e.clientY - dragOffset.y) / scale;
      const newW = Math.max(20, Math.min(300, qrSize.width + deltaX));
      const newH = Math.max(20, Math.min(300, qrSize.height + deltaY));
      setQrSize({ width: newW, height: newH });
      setDragOffset({ x: e.clientX, y: e.clientY });
      return;
    }

    if (isDragging) {
      let nx = (e.clientX - badgeRect.left) / scale - dragOffset.x / scale;
      let ny = (e.clientY - badgeRect.top) / scale - dragOffset.y / scale;
      nx = Math.max(0, Math.min(nx, BADGE_WIDTH - qrSize.width));
      ny = Math.max(0, Math.min(ny, BADGE_HEIGHT - qrSize.height));
      setQrPosition({ x: nx, y: ny });
    }

    if (draggingTextId) {
      let nx = (e.clientX - badgeRect.left) / scale - dragOffset.x / scale;
      let ny = (e.clientY - badgeRect.top) / scale - dragOffset.y / scale;
      nx = Math.max(0, Math.min(nx, BADGE_WIDTH - 50));
      ny = Math.max(0, Math.min(ny, BADGE_HEIGHT - 20));
      setTextElements((prev) =>
        prev.map((el) =>
          el.id === draggingTextId ? { ...el, x: nx, y: ny } : el
        )
      );
    }

    if (draggingImageId) {
      const cur = images.find((i) => i.id === draggingImageId);
      if (cur) {
        let nx = (e.clientX - badgeRect.left) / scale - dragOffset.x / scale;
        let ny = (e.clientY - badgeRect.top) / scale - dragOffset.y / scale;
        nx = Math.max(0, Math.min(nx, BADGE_WIDTH - cur.width));
        ny = Math.max(0, Math.min(ny, BADGE_HEIGHT - cur.height));
        setImages((prev) =>
          prev.map((i) =>
            i.id === draggingImageId ? { ...i, x: nx, y: ny } : i
          )
        );
      }
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDraggingTextId(null);
    setDraggingImageId(null);
    setIsResizing(false);
  };

  const handleReset = () => {
    setQrPosition({ x: 50, y: 50 });
    setQrSize({ width: 35, height: 35 });
    setTextElements([]);
    setBgColor("#ffffff");
    setQrImage(null);
    setImages([]);
    setBgImage(null);
    setSelectedTextId(null);
    setQrText("https://example.com");
  };

  const handleDownload = async () => {
    if (!badgeRef.current) return;
    try {
      setIsResizing(false);
      setIsDragging(false);
      setDraggingTextId(null);
      setDraggingImageId(null);
      await new Promise((r) => setTimeout(r, 100));
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(badgeRef.current, {
        backgroundColor: bgImage ? null : bgColor,
        scale: 2,
        useCORS: true,
      });
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "badge-design.png";
          a.click();
          URL.revokeObjectURL(url);
        }
      });
    } catch (err) {
      console.error("Download error:", err);
      alert("Error downloading badge.");
    }
  };

  const selectedText = textElements.find((el) => el.id === selectedTextId);

  return (
    <div className="min-vh-100 bg-light py-4">
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col">
            <h1 className="h3 fw-bold mb-1">Badge Designer</h1>
            <p className="text-muted mb-0">
              Create custom 4×6 inch badges with QR codes, images, and text
            </p>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-3 order-lg-1 order-2">
            <div className="d-flex flex-column gap-3">
              <div className="card shadow-sm">
                <div className="card-body">
                  <h6 className="fw-bold mb-3">QR Code</h6>
                  <div className="mb-3">
                    <label className="form-label small fw-medium">
                      Generate QR Code
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={qrText}
                      onChange={(e) => setQrText(e.target.value)}
                      placeholder="Enter text or URL"
                    />
                    <div className="form-text text-muted">
                      Enter any text or URL to generate QR code
                    </div>
                  </div>

                  <div>
                    <label className="form-label small fw-medium">
                      QR Size (px)
                    </label>
                    <div className="row g-2">
                      <div className="col-6">
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={Math.round(qrSize.width)}
                          onChange={(e) => {
                            const w = Math.max(
                              20,
                              Math.min(300, Number.parseInt(e.target.value) || 35)
                            );
                            setQrSize((p) => ({ ...p, width: w }));
                          }}
                          min="20"
                          max="300"
                          placeholder="Width"
                        />
                        <div className="form-text text-muted" style={{ fontSize: "10px" }}>
                          Width
                        </div>
                      </div>
                      <div className="col-6">
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={Math.round(qrSize.height)}
                          onChange={(e) => {
                            const h = Math.max(
                              20,
                              Math.min(300, Number.parseInt(e.target.value) || 35)
                            );
                            setQrSize((p) => ({ ...p, height: h }));
                          }}
                          min="20"
                          max="300"
                          placeholder="Height"
                        />
                        <div className="form-text text-muted" style={{ fontSize: "10px" }}>
                          Height
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card shadow-sm">
                <div className="card-body">
                  <h6 className="fw-bold mb-3">Background</h6>
                  <div className="mb-3">
                    <label className="form-label small fw-medium">
                      Background Image
                    </label>
                    <input
                      ref={bgImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleBgImageUpload}
                      className="d-none"
                    />
                    <div className="d-grid gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => bgImageInputRef.current?.click()}
                      >
                        <MdUpload className="me-2" size={16} />
                        {bgImage ? "Change" : "Upload"}
                      </button>
                      {bgImage && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => setBgImage(null)}
                        >
                          <MdDelete className="me-2" size={16} />
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="form-label small fw-medium">
                      Background Color
                    </label>
                    <div className="d-flex gap-2">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        style={{ width: "50px", height: "38px" }}
                      />
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        placeholder="#ffffff"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card shadow-sm">
                <div className="card-body">
                  <h6 className="fw-bold mb-3">Images</h6>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="d-none"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm w-100 mb-3"
                    onClick={() => imageInputRef.current?.click()}
                  >
                    <MdUpload className="me-2" size={16} />
                    Upload Images
                  </button>

                  {images.length > 0 && (
                    <div
                      className="d-flex flex-column gap-2"
                      style={{ maxHeight: "200px", overflowY: "auto" }}
                    >
                      {images.map((img, i) => (
                        <div
                          key={img.id}
                          className="d-flex align-items-center gap-2 p-2 border rounded bg-white"
                        >
                          <img
                            src={img.src || "/placeholder.svg"}
                            alt={`Image ${i + 1}`}
                            className="rounded"
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "cover",
                            }}
                          />
                          <span className="small flex-grow-1">
                            Image {i + 1}
                          </span>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDeleteImage(img.id)}
                            style={{ padding: "4px 8px" }}
                          >
                            <MdDelete size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="card shadow-sm">
                <div className="card-body">
                  <h6 className="fw-bold mb-3">Text Elements</h6>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm w-100 mb-3"
                    onClick={handleAddText}
                  >
                    <MdAdd className="me-2" size={16} />
                    Add Text
                  </button>

                  {textElements.length > 0 && (
                    <div
                      className="d-flex flex-column gap-2"
                      style={{ maxHeight: "200px", overflowY: "auto" }}
                    >
                      {textElements.map((t) => (
                        <div
                          key={t.id}
                          className={`d-flex align-items-center gap-2 p-2 border rounded ${
                            selectedTextId === t.id
                              ? "border-primary bg-primary bg-opacity-10"
                              : "bg-white"
                          }`}
                        >
                          <span
                            className="small flex-grow-1 text-truncate"
                            style={{ maxWidth: "120px" }}
                          >
                            {t.text}
                          </span>
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              selectedTextId === t.id
                                ? "btn-primary"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => setSelectedTextId(t.id)}
                            style={{ padding: "2px 8px", fontSize: "11px" }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDeleteText(t.id)}
                            style={{ padding: "4px 8px" }}
                          >
                            <MdDelete size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="d-grid gap-2">
                <button
                  type="button"
                  className="btn btn-success btn-lg"
                  onClick={handleDownload}
                >
                  <MdDownload className="me-2" size={20} />
                  Download Badge
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  <MdRotateLeft className="me-2" size={16} />
                  Reset All
                </button>
              </div>
            </div>
          </div>

          <div className="col-lg-6 order-lg-2 order-1">
            <div className="card shadow">
              <div className="card-body p-4">
                <div className="d-flex justify-content-center">
                  <div
                    ref={badgeRef}
                    className="position-relative border border-2 border-dark"
                    style={{
                      width: "100%",
                      maxWidth: `${BADGE_WIDTH}px`,
                      aspectRatio: `${BADGE_WIDTH} / ${BADGE_HEIGHT}`,
                      backgroundColor: bgImage ? "transparent" : bgColor,
                      backgroundImage: bgImage ? `url(${bgImage})` : "none",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  >
                    {images.map((img) => (
                      <div
                        key={img.id}
                        className="position-absolute"
                        style={{
                          left: `${img.x}px`,
                          top: `${img.y}px`,
                          width: `${img.width}px`,
                          height: `${img.height}px`,
                          cursor: "move",
                          border:
                            draggingImageId === img.id
                              ? "2px solid #0d6efd"
                              : "1px solid rgba(0,0,0,0.1)",
                        }}
                        onMouseDown={(e) => handleImageMouseDown(e, img.id)}
                      >
                        <img
                          src={img.src || "/placeholder.svg"}
                          alt="Uploaded"
                          className="w-100 h-100"
                          style={{ objectFit: "cover", pointerEvents: "none" }}
                        />
                      </div>
                    ))}

                    {textElements.map((t) => (
                      <div
                        key={t.id}
                        className="position-absolute"
                        style={{
                          left: `${t.x}px`,
                          top: `${t.y}px`,
                          fontSize: `${t.fontSize}px`,
                          fontFamily: t.fontFamily,
                          color: t.color,
                          fontWeight: t.fontWeight,
                          textAlign: t.textAlign,
                          cursor: "move",
                          padding: "4px 8px",
                          backgroundColor:
                            selectedTextId === t.id
                              ? "rgba(13, 110, 253, 0.1)"
                              : "rgba(255, 255, 255, 0.8)",
                          border:
                            selectedTextId === t.id
                              ? "2px solid #0d6efd"
                              : "1px solid rgba(0,0,0,0.1)",
                          borderRadius: "4px",
                        }}
                        onMouseDown={(e) => handleTextMouseDown(e, t.id)}
                      >
                        {t.text}
                      </div>
                    ))}

                    <div
                      className="position-absolute"
                      style={{
                        left: `${qrPosition.x}px`,
                        top: `${qrPosition.y}px`,
                        width: `${qrSize.width}px`,
                        height: `${qrSize.height}px`,
                        border: isDragging || isResizing ? "2px solid #0d6efd" : "1px solid rgba(0,0,0,0.2)",
                        backgroundColor: "white",
                        cursor: "move",
                      }}
                    >
                      <div className="w-100 h-100" onMouseDown={handleMouseDown}>
                        {qrImage && (
                          <img
                            src={qrImage}
                            alt="QR Code"
                            className="w-100 h-100"
                            style={{ objectFit: "contain", pointerEvents: "none" }}
                          />
                        )}
                      </div>

                      {!isResizing && (
                        <div
                          onMouseDown={handleResizeMouseDown}
                          style={{
                            position: "absolute",
                            right: "-6px",
                            bottom: "-6px",
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#0d6efd",
                            border: "2px solid white",
                            borderRadius: "50%",
                            cursor: "nwse-resize",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-3 order-lg-3 order-3">
            {selectedText ? (
              <div className="card shadow-sm border-primary">
                <div className="card-body">
                  <h6 className="fw-bold mb-3 text-primary">
                    Edit Selected Text
                  </h6>

                  <div className="mb-3">
                    <label className="form-label small fw-medium">
                      Content
                    </label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={3}
                      value={selectedText.text}
                      onChange={(e) =>
                        handleTextChange(selectedText.id, e.target.value)
                      }
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-medium">
                      Font Family
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedText.fontFamily}
                      onChange={(e) =>
                        handleTextStyleChange(
                          selectedText.id,
                          "fontFamily",
                          e.target.value
                        )
                      }
                    >
                      {fontFamilies.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <label className="form-label small fw-medium">Size</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={selectedText.fontSize}
                        onChange={(e) =>
                          handleTextStyleChange(
                            selectedText.id,
                            "fontSize",
                            Number.parseInt(e.target.value) || 16
                          )
                        }
                        min="8"
                        max="72"
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-medium">Weight</label>
                      <select
                        className="form-select form-select-sm"
                        value={selectedText.fontWeight}
                        onChange={(e) =>
                          handleTextStyleChange(
                            selectedText.id,
                            "fontWeight",
                            e.target.value
                          )
                        }
                      >
                        {fontWeights.map((w) => (
                          <option key={w.value} value={w.value}>
                            {w.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-medium">Color</label>
                    <div className="d-flex gap-2">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={selectedText.color}
                        onChange={(e) =>
                          handleTextStyleChange(
                            selectedText.id,
                            "color",
                            e.target.value
                          )
                        }
                        style={{ width: "50px", height: "38px" }}
                      />
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace"
                        value={selectedText.color}
                        onChange={(e) =>
                          handleTextStyleChange(
                            selectedText.id,
                            "color",
                            e.target.value
                          )
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label small fw-medium">
                      Alignment
                    </label>
                    <div className="btn-group w-100">
                      {["left", "center", "right"].map((a) => (
                        <button
                          key={a}
                          type="button"
                          className={`btn btn-sm ${
                            selectedText.textAlign === a ? "btn-primary" : "btn-outline-primary"
                          }`}
                          onClick={() =>
                            handleTextStyleChange(selectedText.id, "textAlign", a)
                          }
                        >
                          {a.charAt(0).toUpperCase() + a.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="card shadow-sm bg-light">
                <div className="card-body text-center py-5">
                  <div className="text-muted mb-3">
                    <MdAdd size={48} className="opacity-25" />
                  </div>
                  <h6 className="fw-medium mb-2">No Text Selected</h6>
                  <p className="small text-muted mb-0">
                    Add a text element or click an existing one to edit its
                    properties
                  </p>
                </div>
              </div>
            )}

            <div className="card shadow-sm mt-3">
              <div className="card-body">
                <h6 className="fw-bold mb-3">Specifications</h6>
                <div className="small">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Size:</span>
                    <span className="fw-medium">4×6 inches</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Resolution:</span>
                    <span className="fw-medium">384×576px</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">DPI:</span>
                    <span className="fw-medium">96</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Testing;